import {
  ArrowDropDownCircleOutlined,
  CheckBox,
  ControlPointDuplicate,
  Image,
  Input,
  MyLocationOutlined,
  KeyboardVoice,
  LinearScale,
  LocationOn,
  Dashboard,
  ViewStream
} from "@mui/icons-material";
import {IconFile} from "@tabler/icons-react";

import audioIconSvg from "../../assets/icons/audio-icon.svg"
import codeIconSvg from "../../assets/icons/code-icon.svg"
import fileIconSvg from "../../assets/icons/file-icon.svg"
import pdfIconSvg from "../../assets/icons/pdf-icon.svg"
import presentationIconSvg from "../../assets/icons/presentation-icon.svg"
import spreadsheetIconSvg from "../../assets/icons/spreadsheet-icon.svg"
import textIconSvg from "../../assets/icons/text-icon.svg"
import videoIconSvg from "../../assets/icons/video-icon.svg"
import imageIconSvg from "../../assets/icons/image-icon.svg"
import zipIconSvg from "../../assets/icons/zip-icon.svg"

// Constants for question type IDs
export const QuestionTypes = {
  SECTION: 'section',
  TEXT: 'text',
  VOICE: 'voice',
  MULTICHOICE: 'multichoice',
  CHECKBOXES: 'checkboxes',
  DROPDOWN: 'dropdown',
  IMAGE: 'image',
  SLIDER: 'slider',
  MAPINPUT: 'mapInput',
  LOCATE_USER: 'locateuser',
  FILE_UPLOAD: 'fileUpload',
  IMAGE_GALLERY: 'imageGallery',
};

// Unified metadata object for each question type
export const questionTypesDescription = {
  [QuestionTypes.SECTION]: {
    type: QuestionTypes.SECTION,
    name: "Section (Title, Desc)",
    description: "To use as a title screen or section divider",
    icon: ViewStream
  },
  [QuestionTypes.TEXT]: {
    type: QuestionTypes.TEXT,
    name: "Text",
    description: "Type a brief, open-ended text response",
    icon: Input
  },
  [QuestionTypes.VOICE]: {
    type: QuestionTypes.VOICE,
    name: "Voice",
    description: "Record a voice response (that is transcribed and translated to English) using the device’s microphone",
    icon: KeyboardVoice
  },
  [QuestionTypes.MULTICHOICE]: {
    type: QuestionTypes.MULTICHOICE,
    name: "Multiple Choice (Single Select)",
    description: "Select one answer from a list of options",
    icon: ControlPointDuplicate
  },
  [QuestionTypes.CHECKBOXES]: {
    type: QuestionTypes.CHECKBOXES,
    name: "Checkboxes (Multi Select)",
    description: "Select multiple answers from a list of options",
    icon: CheckBox
  },
  [QuestionTypes.DROPDOWN]: {
    type: QuestionTypes.DROPDOWN,
    name: "Dropdown (Multi Select)",
    description: "Select multiple answers from a dropdown of options",
    icon: ArrowDropDownCircleOutlined
  },
  [QuestionTypes.IMAGE]: {
    type: QuestionTypes.IMAGE,
    name: "Image Grid (Single or Multi Select)",
    description: "Select image(s) from a grid of images",
    icon: Image
  },
  [QuestionTypes.IMAGE_GALLERY]: {
    type: QuestionTypes.IMAGE_GALLERY,
    name: "Image Grid (Single or Multi Select)",
    description: "Select image(s) from a grid of images",
    icon: Dashboard
  },
  [QuestionTypes.SLIDER]: {
    type: QuestionTypes.SLIDER,
    name: "Number Scale",
    description: "Rate on a sliding scale (e.g., 1–5, agree/disagree)",
    icon: LinearScale
  },
  [QuestionTypes.MAPINPUT]: {
    type: QuestionTypes.MAPINPUT,
    name: "Map Input",
    description: "Plot points on a map",
    icon: LocationOn
  },
  [QuestionTypes.LOCATE_USER]: {
    type: QuestionTypes.LOCATE_USER,
    name: "Geolocate User",
    description: "Get the respondent’s mobile device location",
    icon: MyLocationOutlined
  },
  [QuestionTypes.FILE_UPLOAD]: {
    type: QuestionTypes.FILE_UPLOAD,
    name: "File Upload",
    description: "Upload file(s)",
    icon: IconFile // You can assign an icon if needed
  },
};

// Reordered and exported list (used in menus, etc.)
export const questionTypeList = [
  questionTypesDescription[QuestionTypes.SECTION],
  questionTypesDescription[QuestionTypes.TEXT],
  questionTypesDescription[QuestionTypes.VOICE],
  questionTypesDescription[QuestionTypes.MULTICHOICE],
  questionTypesDescription[QuestionTypes.CHECKBOXES],
  questionTypesDescription[QuestionTypes.DROPDOWN],
  questionTypesDescription[QuestionTypes.IMAGE],
  questionTypesDescription[QuestionTypes.SLIDER],
  questionTypesDescription[QuestionTypes.MAPINPUT],
  questionTypesDescription[QuestionTypes.LOCATE_USER],
  questionTypesDescription[QuestionTypes.FILE_UPLOAD],
];

export const fileTypes = [
  {
    type: 'Image',
    icon: imageIconSvg,
    extensions: [
      { extension: '.png', selected: false },
      { extension: '.jpg', selected: false },
      { extension: '.jpeg', selected: false },
      { extension: '.gif', selected: false },
      { extension: '.webp', selected: false },
      { extension: '.tiff', selected: false },
    ],
  },
  {
    type: 'Video',
    icon: videoIconSvg,
    extensions: [
      { extension: '.mp4', selected: false },
      { extension: '.avi', selected: false },
      { extension: '.mov', selected: false },
      { extension: '.webm', selected: false },
      { extension: '.mkv', selected: false },
    ],
  },
  {
    type: 'Document',
    icon: textIconSvg,
    extensions: [
      { extension: '.pdf', selected: false },
      { extension: '.doc', selected: false },
      { extension: '.docx', selected: false },
      { extension: '.txt', selected: false },
      { extension: '.md', selected: false },
      { extension: '.rtf', selected: false },
      { extension: '.odt', selected: false },
      { extension: '.pages', selected: false },
    ],
  },
  {
    type: 'Audio',
    icon: audioIconSvg,
    extensions: [
      { extension: '.mp3', selected: false },
      { extension: '.wav', selected: false },
      { extension: '.ogg', selected: false },
      { extension: '.aac', selected: false },
      { extension: '.flac', selected: false },
      { extension: '.m4a', selected: false },
    ],
  },
  {
    type: 'Archive',
    icon: zipIconSvg,
    extensions: [
      { extension: '.zip', selected: false },
      { extension: '.rar', selected: false },
      { extension: '.7z', selected: false },
      { extension: '.tar', selected: false },
      { extension: '.gz', selected: false },
    ],
  },
  {
    type: 'Presentation',
    icon: presentationIconSvg,
    extensions: [
      { extension: '.ppt', selected: false },
      { extension: '.pptx', selected: false },
      { extension: '.key', selected: false },
      { extension: '.odp', selected: false },
    ],
  },
  {
    type: 'Code',
    icon: codeIconSvg,
    extensions: [
      { extension: '.js', selected: false },
      { extension: '.py', selected: false },
      { extension: '.html', selected: false },
      { extension: '.css', selected: false },
      { extension: '.java', selected: false },
      { extension: '.c', selected: false },
      { extension: '.cpp', selected: false },
      { extension: '.php', selected: false },
      { extension: '.json', selected: false },
      { extension: '.xml', selected: false },
    ],
  },
  {
    type: 'Spreadsheet',
    icon: spreadsheetIconSvg,
    extensions: [
      { extension: '.xls', selected: false },
      { extension: '.xlsx', selected: false },
      { extension: '.csv', selected: false },
      { extension: '.ods', selected: false },
      { extension: '.numbers', selected: false },
    ],
  },
  {
    type: 'Other',
    icon: fileIconSvg,
    extensions: [
      { extension: '.psd', selected: false },
      { extension: '.ai', selected: false },
      { extension: '.dwg', selected: false },
      { extension: '.sql', selected: false },
    ],
  },
];

export const QuestionStatusType = {
  TRANSLATED: "TRANSLATED",
  TRANSLATING: "TRANSLATING",
  FAILED: "FAILED",
}
